/* sporeling — eight frames of one lurching step.
 *
 * The two frames the game has been alternating are not arbitrary: the original
 * `sporeling_0` is a PASSING pose (stem at full extension, cap held tight and
 * level, the foot planted on a solid base) and the original `sporeling_1` is a
 * CONTACT pose (the body a row lower, the neck eaten by the squat, the cap
 * flared to the full sixteen, the foot split into four splayed toes). Those are
 * the two extremes of a step, so they are kept here verbatim at phases 0 and 4
 * and the six between them carry the weight from one to the other.
 *
 * A mushroom does not walk, it TIPS — the mass is all in the cap and the stem
 * is a lever under it. So the cycle is a waddle, and it runs on three things
 * that deliberately do NOT change on the same frames, because a cycle whose
 * parts all move together is a cycle with two poses in it:
 *
 *   CAP SWING (x): 0, -1, -1, 0, 0(flared), +1, +1, +1. The cap leads the fall
 *   and lags the landing, and it is still a pixel right at phase 7 — it comes
 *   level only on frame 0, so the transition that closes the loop is not a dead
 *   one. Frame 7 also splits the cap: the crown is already back at centre while
 *   the body of the cap is not, which is a cap levelling out rather than
 *   sliding.
 *
 *   BOB (y): the cap top rides rows 1, 1, 2, 2, 2, 2, 1, 1. Two rows is all a
 *   16px cell has, so the drop (1→2) and the rise (5→6) are honest whole-pixel
 *   events and they cost what they cost — 81 px each. Everything else in the
 *   cycle exists so that those two are not the only frames that move.
 *
 *   SQUASH: the spread of the belly travels DOWN the body and out. It arrives
 *   at the top of the belly on frame 2 (the weight coming down the stem),
 *   reaches the collar and all three belly rows at contact on frame 4, has
 *   drained to the bottom two rows by frame 5 and to the bottom row alone by
 *   frame 6, and is gone by frame 7.
 *
 * The leg is the counterweight, sheared off the body's centre in whichever
 * direction is taking the weight: LEFT under the pre-contact lean on frame 3,
 * RIGHT under the landing on frames 5 and 6, back under centre on frame 7 —
 * where it arrives a frame before the cap does, because feet land first.
 *
 *   0  passing. Level, centred, foot planted on a solid base. (keyframe)
 *   1  the lean. The cap swings a pixel LEFT to start the fall; the right heel
 *      lifts off the base because the weight has already left it.
 *   2  the squat. The body drops a row onto the planted foot — the STEM loses a
 *      row, the foot does not move — the shoulders follow the cap left, the
 *      squash arrives at the top of the belly, the base breaks in two.
 *   3  the split. The cap swings back through centre while the leg shears a
 *      pixel LEFT onto the split base: the last frame that still touches down.
 *   4  contact. Cap flared, belly spread, toes splayed, nothing on row 15.
 *      (keyframe)
 *   5  the settle. The flare collapses and flops a pixel RIGHT — the cap always
 *      arrives after the feet do — the leg shears right and replants, and the
 *      squash has drained to the bottom of the belly.
 *   6  the rise. The body comes back up a row over the planted right-shifted
 *      leg, the stem grows its row back, and the last of the spread is in the
 *      bottom belly row alone.
 *   7  the recover. The leg swings back under centre and takes the weight
 *      first; the cap is still a pixel right with only its crown levelled, and
 *      the left heel has not settled yet — so frame 0 is the one thing still
 *      left to happen, not a repeat of something that already has.
 *
 * Nothing here interpolates: every frame is the previous one with whole pixels
 * moved. Ink stays inside 121–125 across all eight, so the silhouette never
 * thins or bloats, and the steps are 27/81/46/31/45/81/25/25 — a spread of 1.8x
 * the median, the same band as wisp and warden32. */

export const sporeling_0 = [
  '................',
  '....oooooooo....',
  '..oopppppppppo..',
  '.oppppppppppppo.',
  '.oppPpppppPppo..',
  '.oppppppppppppo.',
  '..oPPPPPPPPPPo..',
  '...ooouuuuooo...',
  '.....ouuuuo.....',
  '.....uodduo.....',
  '.....ouuuuo.....',
  '......ouuo......',
  '......ouuo......',
  '......ouuo......',
  '.....oouuoo.....',
  '.....oooooo.....',
];

export const sporeling_1 = [
  '................',
  '...oooooooo.....',
  '.oopppppppppo...',
  'oppppppppppppo..',
  'oppPpppppPppo...',
  'oppppppppppppo..',
  '.oPPPPPPPPPPo...',
  '...ooouuuuooo...',
  '.....ouuuuo.....',
  '.....uodduo.....',
  '.....ouuuuo.....',
  '......ouuo......',
  '......ouuo......',
  '......ouuo......',
  '.....oouuoo.....',
  '.....ooooo......',
];

export const sporeling_2 = [
  '................',
  '................',
  '...oooooooo.....',
  '.oopppppppppo...',
  'oppppppppppppo..',
  'oppPpppppPppo...',
  'oppppppppppppo..',
  '.oPPPPPPPPPPo...',
  '..ooouuuuooo....',
  '....oouuuuoo....',
  '.....uodduo.....',
  '.....ouuuuo.....',
  '......ouuo......',
  '......ouuo......',
  '.....oouuoo.....',
  '.....oo..oo.....',
];

export const sporeling_3 = [
  '................',
  '................',
  '....oooooooo....',
  '..oopppppppppo..',
  '.oppppppppppppo.',
  '.oppPpppppPppo..',
  '.oppppppppppppo.',
  '..oPPPPPPPPPPo..',
  '...ooouuuuooo...',
  '....oouuuuoo....',
  '.....uodduo.....',
  '.....ouuuuo.....',
  '.....ouuo.......',
  '.....ouuo.......',
  '....oouuoo......',
  '....oo..oo......',
];

export const sporeling_4 = [
  '................',
  '................',
  '....oooooooo....',
  '..oopppppppppo..',
  '..pppppppppppp..',
  '..ppPpppppPpp...',
  'ooppppppppppppoo',
  '.ooPPPPPPPPPPoo.',
  '..oooouuuuoooo..',
  '....oouuuuoo....',
  '....uuodduoo....',
  '....oouuuuoo....',
  '......ouuo......',
  '......ouuo......',
  '....o.o..o.o....',
  '................',
];

export const sporeling_5 = [
  '................',
  '................',
  '.....oooooooo...',
  '...oopppppppppo.',
  '..oppppppppppppo',
  '..oppPpppppPppo.',
  '..oppppppppppppo',
  '...oPPPPPPPPPPo.',
  '...ooouuuuooo...',
  '.....ouuuuo.....',
  '....uuodduoo....',
  '....oouuuuoo....',
  '.......ouuo.....',
  '.......ouuo.....',
  '......oouuoo....',
  '......oo..oo....',
];

export const sporeling_6 = [
  '................',
  '.....oooooooo...',
  '...oopppppppppo.',
  '..oppppppppppppo',
  '..oppPpppppPppo.',
  '..oppppppppppppo',
  '...oPPPPPPPPPPo.',
  '....ooouuuuooo..',
  '.....ouuuuo.....',
  '.....uodduo.....',
  '....oouuuuoo....',
  '.......ouuo.....',
  '.......ouuo.....',
  '.......ouuo.....',
  '......oouuoo....',
  '.......oooo.....',
];

export const sporeling_7 = [
  '................',
  '....oooooooo....',
  '...oopppppppppo.',
  '..oppppppppppppo',
  '..oppPpppppPppo.',
  '..oppppppppppppo',
  '...oPPPPPPPPPPo.',
  '...ooouuuuooo...',
  '.....ouuuuo.....',
  '.....uodduo.....',
  '.....ouuuuo.....',
  '......ouuo......',
  '......ouuo......',
  '......ouuo......',
  '.....oouuoo.....',
  '......ooooo.....',
];
